import React, { createContext, useContext, useState, ReactNode } from 'react'

export type AlertType = 'success' | 'error' | 'warning' | 'info'

export interface Alert {
  id: string
  type: AlertType
  message: string
  duration?: number
}

interface AlertContextType {
  alerts: Alert[]
  addAlert: (type: AlertType, message: string, duration?: number) => void
  removeAlert: (id: string) => void
  clearAlerts: () => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

interface AlertProviderProps {
  children: ReactNode
}

const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([])

  const addAlert = (type: AlertType, message: string, duration = 5000) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newAlert: Alert = { id, type, message, duration }
    
    setAlerts(prev => [...prev, newAlert])

    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id)
      }, duration)
    }
  }

  const removeAlert = (id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id))
  }

  const clearAlerts = () => {
    setAlerts([])
  }

  const value: AlertContextType = {
    alerts,
    addAlert,
    removeAlert,
    clearAlerts
  }

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  )
}

export const useAlerts = (): AlertContextType => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertProvider')
  }
  return context
}

export default AlertProvider
